import * as React from "react";
import { Image, LayoutTemplate, MessageSquareQuote } from "lucide-react";
import { Button } from "@/components/ui";
import { STYLE_CHIPS, STYLE_GROUPS } from "../styleChips";
import { GUIDE_LINKS } from "./guideLinks";
import { HelpCallout, HelpCards, HelpDialog, HelpDialogProps, HelpFooter, HelpSection } from "./HelpDialog";
import { HowBackgroundsWorkDialog } from "./HowBackgroundsWorkDialog";

const LEAVE_TO_THE_POST = [
  { source: "Player", fields: "Name, title, headshot, NMLS and compliance line" },
  { source: "Branding", fields: "Background and text colors, accent color, branch logo, disclaimer" },
  {
    source: "Review (review designs)",
    fields: "The quote with its best phrase highlighted, stars, reviewer name, platform",
  },
];

// Short enough to sit two to a line in the half-width card.
const EXAMPLES = [
  "frosted glass card over a home photo",
  "big bold headline, no photo",
  "overlap the headshot on the quote card",
  "highlight the best phrase",
];

const BRIEF = [
  {
    title: "Be specific",
    body: (
      <div className="space-y-3">
        <p>Name the layout, imagery, type, mood, and where things sit on the card.</p>
        <div className="space-y-1.5">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Try asking for</div>
          <ul className="flex flex-wrap gap-1.5">
            {EXAMPLES.map(example => (
              <li
                key={example}
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-white px-2.5 py-1 text-sm text-slate-700 shadow-xs"
              >
                <MessageSquareQuote className="size-3.5 shrink-0 text-primary" aria-hidden="true" />
                {example}
              </li>
            ))}
          </ul>
        </div>
      </div>
    ),
  },
  {
    title: "Leave to the post",
    body: (
      <div className="space-y-2">
        <p>Filled in for each Player when they share, so you never ask for them.</p>
        <ul className="space-y-1">
          {LEAVE_TO_THE_POST.map(row => (
            <li key={row.source}>
              <strong className="text-slate-700">{row.source}</strong> · <span>{row.fields}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
];

const Pill: React.FC<React.PropsWithChildren> = ({ children }) => (
  <span className="rounded-full border border-slate-200 bg-white px-3 py-0.5 text-sm text-slate-700">{children}</span>
);

export const WhatCanIAskDialog: React.FC<HelpDialogProps> = ({ open, onOpenChange }) => {
  const [backgroundsOpen, setBackgroundsOpen] = React.useState(false);
  return (
    <>
      <HelpDialog
        open={open}
        onOpenChange={onOpenChange}
        title="What can I ask for?"
        showLogo={false}
        description="Ask for the look. The words come from the review and the player."
        size="xl"
        footer={<HelpFooter guideUrl={GUIDE_LINKS.ask} onClose={() => onOpenChange(false)} />}
      >
        <HelpCards items={BRIEF} columns={2} />

        <HelpSection title="Pick it on the New design screen" icon={LayoutTemplate}>
          <p className="text-sm text-slate-600">
            Single image or carousel (2 to 5 slides) · Square 1:1, Portrait 4:5, or Story 9:16 · Upload a reference
            image to match a style
          </p>
          <p className="text-sm text-slate-600">
            You only see the styles that fit the kind of post and format you picked.
          </p>
          <div className="space-y-3">
            {STYLE_GROUPS.map(group => (
              <div key={group.id} className="grid grid-cols-1 gap-1.5 md:grid-cols-[110px_1fr] md:items-baseline">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{group.label}</div>
                <div className="flex flex-wrap gap-1.5">
                  {STYLE_CHIPS.filter(chip => chip.group === group.id).map(chip => (
                    <Pill key={chip.id}>{chip.label}</Pill>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </HelpSection>

        <HelpSection title="Backgrounds" icon={Image}>
          <p className="text-sm text-slate-600">
            Ask for a kind of photo, like "a modern kitchen at golden hour". You can swap or add backgrounds afterwards.{" "}
            <Button variant="link" size="inline" onClick={() => setBackgroundsOpen(true)}>
              How backgrounds work
            </Button>
          </p>
        </HelpSection>

        <HelpCallout>
          Nothing is final until you approve it. Refine one slide or all of them, in the same plain words.
        </HelpCallout>
      </HelpDialog>
      <HowBackgroundsWorkDialog open={backgroundsOpen} onOpenChange={setBackgroundsOpen} />
    </>
  );
};
